import { useMemo } from "react";
import { formatNumber, formatPercent } from "../../../../utils/format";

/**
 * Ranked horizontal-bar distribution for a list of tags.
 * - Each row: color dot + tag name + count + % of total (relative to `total`)
 * - Bar width is proportional to count / maxCount
 * - Top N visible by default; beyond that, collapse into "+ N tag khác"
 */
const TagDistributionList = ({
  tags = [],
  total = 0,
  loading,
  topN = 10,
  emptyLabel = "Chưa có tag nào",
  zeroCountLabel = "Không có contact nào trong khoảng này",
}) => {
  const { visible, hidden, maxCount } = useMemo(() => {
    const list = (tags || []).filter((t) => Number(t.count) > 0);
    const max = list.reduce((m, t) => Math.max(m, Number(t.count) || 0), 0);
    return {
      visible: list.slice(0, topN),
      hidden: list.slice(topN),
      maxCount: max || 1,
    };
  }, [tags, topN]);

  if (loading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="h-8 rounded bg-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (!tags || tags.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-6 text-center">
        {emptyLabel}
      </div>
    );
  }

  if (visible.length === 0) {
    return (
      <div className="text-sm text-gray-500 py-6 text-center">
        {zeroCountLabel}
      </div>
    );
  }

  const hiddenTotal = hidden.reduce(
    (sum, t) => sum + (Number(t.count) || 0),
    0,
  );

  return (
    <div className="space-y-1.5">
      {visible.map((t) => {
        const count = Number(t.count) || 0;
        const widthPct = Math.max(2, (count / maxCount) * 100);
        return (
          <div
            key={t.id ?? t.name}
            className="group relative h-8 flex items-center"
            title={`${t.name}: ${formatNumber(count)}`}
          >
            <div
              className="absolute inset-y-0 left-0 rounded-md opacity-75 group-hover:opacity-100 transition-opacity"
              style={{
                width: `${widthPct}%`,
                backgroundColor: t.color || "#9ca3af",
              }}
            />
            <div className="relative flex items-center w-full px-2.5 gap-2">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0 ring-2 ring-white"
                style={{ backgroundColor: t.color || "#9ca3af" }}
              />
              <span className="text-xs font-medium text-gray-900 truncate flex-1">
                {t.name}
              </span>
              <span className="text-xs tabular-nums text-gray-700 shrink-0">
                {formatNumber(count)}
              </span>
              <span className="text-[11px] tabular-nums text-gray-500 shrink-0 w-10 text-right">
                {formatPercent(count, total || maxCount)}
              </span>
            </div>
          </div>
        );
      })}
      {hidden.length > 0 && (
        <div className="text-[11px] text-gray-500 pl-3 pt-1">
          + {hidden.length} tag khác ({formatNumber(hiddenTotal)} contact)
        </div>
      )}
    </div>
  );
};

export default TagDistributionList;
